import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowLeft, User, Car, Upload, Lock, Eye, EyeOff, Phone, Mail, Bike, Camera, Check } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { registerDriver } from '../../services/authService';
import { useCountry } from '../../contexts/CountryContext';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

const EnhancedDriverAuth = ({ onBack, onAuthSuccess }) => {
  const ctx = useCountry();
  const [step, setStep] = useState('driverType'); // driverType, details, vehicle
  const [driverType, setDriverType] = useState(''); // 'vehicle' or 'bike'
  const [formData, setFormData] = useState({
    phone: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    vehicleMake: '',
    vehicleModel: '',
    vehiclePlate: '',
    vehicleColor: '',
    vehicleYear: '',
    vehicleCategory: 'economy' // Default to economy
  });

  // Driver registration always uses light mode
  React.useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);
  const [vehicleImages, setVehicleImages] = useState({
    front: null,
    side: null,
    back: null
  });
  const [driverFacePhoto, setDriverFacePhoto] = useState(null); // Driver face photo
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmitDetails = (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.username || !formData.password || !formData.phone) {
      toast({ title: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    
    const nationalPhone = formData.phone.replace(/^0+/, '');
    if (nationalPhone.length !== 9) {
      toast({ title: 'Invalid phone number', description: 'Enter your 10-digit number starting with 0 (e.g. 0991 234 567)', variant: 'destructive' });
      return;
    }
    
    if (formData.username.length < 3) {
      toast({ title: 'Username must be at least 3 characters', variant: 'destructive' });
      return;
    }
    
    if (formData.password.length < 6) {
      toast({ title: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    
    setStep('vehicle');
  };

  const handleImageUpload = async (position, e) => {
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // Use Capacitor Camera API on mobile
      try {
        const image = await CapacitorCamera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt, // Let user choose camera or gallery
          width: 1920,
          height: 1080,
          correctOrientation: true
        });

        setVehicleImages({...vehicleImages, [position]: image.dataUrl});

        toast({
          title: 'Photo captured',
          description: `${position.charAt(0).toUpperCase() + position.slice(1)} photo added successfully`
        });
      } catch (error) {
        console.error('Camera error:', error);
        if (error.message !== 'User cancelled photos app') {
          toast({
            title: 'Camera Error',
            description: 'Failed to capture photo. Please try again.',
            variant: 'destructive'
          });
        }
      }
    } else {
      // Use file input on web
      const file = e.target.files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: 'Invalid file type',
            description: 'Please upload an image file',
            variant: 'destructive'
          });
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: 'File too large',
            description: 'Please upload an image smaller than 5MB',
            variant: 'destructive'
          });
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          setVehicleImages({...vehicleImages, [position]: reader.result});
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleFacePhotoUpload = async (e) => {
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // Use Capacitor Camera API on mobile
      try {
        const image = await CapacitorCamera.getPhoto({
          quality: 90,
          allowEditing: true, // Allow cropping for face photo
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt,
          width: 800,
          height: 800,
          correctOrientation: true
        });

        setDriverFacePhoto(image.dataUrl);

        toast({
          title: 'Photo captured',
          description: 'Driver face photo added successfully'
        });
      } catch (error) {
        console.error('Camera error:', error);
        if (error.message !== 'User cancelled photos app') {
          toast({
            title: 'Camera Error',
            description: 'Failed to capture photo. Please try again.',
            variant: 'destructive'
          });
        }
      }
    } else {
      // Use file input on web
      const file = e.target.files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: 'Invalid file type',
            description: 'Please upload an image file',
            variant: 'destructive'
          });
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: 'File too large',
            description: 'Please upload an image smaller than 5MB',
            variant: 'destructive'
          });
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          setDriverFacePhoto(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCompleteRegistration = async () => {
    // Mandatory face photo validation for all drivers
    if (!driverFacePhoto) {
      toast({ title: 'Driver face photo is required', variant: 'destructive' });
      return;
    }

    // Validation based on driver type
    if (driverType === 'vehicle') {
      // Full validation for vehicle drivers
      if (!formData.vehicleMake || !formData.vehicleModel || !formData.vehiclePlate || !formData.vehicleColor) {
        toast({ title: 'Please fill in all vehicle details', variant: 'destructive' });
        return;
      }

      if (!vehicleImages.front || !vehicleImages.side || !vehicleImages.back) {
        toast({ title: 'Please upload all vehicle images', variant: 'destructive' });
        return;
      }

      if (!formData.vehicleYear) {
        toast({ title: 'Please enter the vehicle year', variant: 'destructive' });
        return;
      }
    } else if (driverType === 'bike') {
      // Minimal validation for bike drivers
      if (!formData.vehiclePlate) {
        toast({ title: 'Please enter your bike registration/plate number', variant: 'destructive' });
        return;
      }
    }

    setLoading(true);

    const userData = {
      phone: formData.phone.replace(/^0+/, ''),
      country_code: ctx?.callingCode && ctx.callingCode !== '+' ? ctx.callingCode : '+265',
      country: ctx?.detectedIso3 || 'MWI',
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email || null,
      username: formData.username,
      password: formData.password,
      driver_type: driverType, // Add driver type
      facePhoto: driverFacePhoto, // Add face photo
      vehicle: {
        type: driverType, // 'vehicle' or 'bike'
        make: driverType === 'vehicle' ? formData.vehicleMake : 'Bike',
        model: driverType === 'vehicle' ? formData.vehicleModel : formData.vehicleModel || 'Standard',
        plate: formData.vehiclePlate,
        color: driverType === 'vehicle' ? formData.vehicleColor : (formData.vehicleColor || 'N/A'),
        year: driverType === 'vehicle' ? formData.vehicleYear : null,
        category: driverType === 'vehicle' ? formData.vehicleCategory : 'bike', // Add category
        images: driverType === 'vehicle' ? vehicleImages : {}
      }
    };

    const result = await registerDriver(userData);
    setLoading(false);
    
    if (result.success) {
      toast({ 
        title: 'Registration Successful!', 
        description: 'You can now log in and start accepting rides'
      });
      onAuthSuccess({...result.user, vehicle: result.vehicle});
    } else {
      toast({
        title: 'Registration Failed',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full shadow-2xl">
        <div className="p-8">
          <button 
            onClick={() => {
              if (step === 'driverType') {
                onBack();
              } else if (step === 'details') {
                setStep('driverType');
              } else if (step === 'vehicle') {
                setStep('details');
              }
            }} 
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              {step === 'driverType' ? (
                <Car className="w-8 h-8 text-white" strokeWidth={2.5} />
              ) : step === 'details' ? (
                <User className="w-8 h-8 text-white" strokeWidth={2.5} />
              ) : (
                driverType === 'bike' ? (
                  <Bike className="w-8 h-8 text-white" strokeWidth={2.5} />
                ) : (
                  <Car className="w-8 h-8 text-white" strokeWidth={2.5} />
                )
              )}
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {step === 'driverType' ? 'Choose Driver Type' : 
               step === 'details' ? 'Driver Registration' : 
               driverType === 'bike' ? 'Bike Details' : 'Vehicle Details'}
            </h2>
            <p className="text-gray-600">
              {step === 'driverType' ? 'Select what you will be driving' :
               step === 'details' ? 'Create your account to start driving' : 
               driverType === 'bike' ? 'Tell us about your bike' : 'Tell us about your vehicle'}
            </p>
          </div>

          {/* Driver Type Selection Step */}
          {step === 'driverType' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vehicle Driver Option */}
                <button
                  onClick={() => {
                    setDriverType('vehicle');
                    setStep('details');
                  }}
                  className="group relative overflow-hidden rounded-2xl border-2 border-gray-200 hover:border-green-500 bg-white p-8 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-500 transition-colors">
                      <Car className="w-10 h-10 text-green-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Vehicle Driver</h3>
                      <p className="text-sm text-gray-600">
                        Drive a car, van, or other vehicle
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Requires: License, vehicle details & photos
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                </button>

                {/* Bike Driver Option */}
                <button
                  onClick={() => {
                    setDriverType('bike');
                    setStep('details');
                  }}
                  className="group relative overflow-hidden rounded-2xl border-2 border-gray-200 hover:border-green-500 bg-white p-8 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-green-500 transition-colors">
                      <Bike className="w-10 h-10 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Bike Driver</h3>
                      <p className="text-sm text-gray-600">
                        Drive a motorcycle or bike
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Simplified registration process
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 'details' && (
            <form onSubmit={handleSubmitDetails} className="space-y-4">
              {/* Phone Number */}
              <div>
                <Label htmlFor="phone" className="text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number *
                </Label>
                <div className="flex mt-2">
                  <div className="bg-gray-100 px-4 py-3 rounded-l-lg border border-r-0 border-gray-300 flex items-center">
                    <span className="text-gray-700 font-medium" data-testid="driver-calling-code">{ctx?.callingCode && ctx.callingCode !== '+' ? ctx.callingCode : '+265'}</span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="0991 234 567"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    className="rounded-l-none"
                    required
                  />
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-gray-700">First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-gray-700">Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="middleName" className="text-gray-700">Middle Name (Optional)</Label>
                <Input
                  id="middleName"
                  type="text"
                  placeholder="Enter middle name"
                  value={formData.middleName}
                  onChange={(e) => setFormData({...formData, middleName: e.target.value})}
                  className="mt-2"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address (Optional)
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-2"
                />
              </div>

              {/* Username */}
              <div>
                <Label htmlFor="username" className="text-gray-700">Username *</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Choose a username"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                  className="mt-2"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers and underscores only</p>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="text-gray-700 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password *
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-gray-700">Confirm Password *</Label>
                  <div className="relative mt-2">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold mt-6"
              >
                Continue to Vehicle Details
              </Button>
            </form>
          )}

          {step === 'vehicle' && (
            <div className="space-y-6">
              {/* Driver Face Photo - Required for all drivers */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <Label className="text-gray-800 font-semibold text-lg mb-3 block flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Driver Face Photo *
                </Label>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a clear photo of your face. This will be shown to riders and helps build trust.
                </p>
                
                {!Capacitor.isNativePlatform() && (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFacePhotoUpload}
                    className="hidden"
                    id="driver-face-photo"
                  />
                )}
                
                <label 
                  htmlFor={Capacitor.isNativePlatform() ? undefined : "driver-face-photo"}
                  onClick={Capacitor.isNativePlatform() ? () => handleFacePhotoUpload(null) : undefined}
                  className="cursor-pointer"
                >
                  {driverFacePhoto ? (
                    <div className="relative inline-block">
                      <img 
                        src={driverFacePhoto} 
                        alt="Driver face" 
                        className="w-40 h-40 object-cover rounded-full border-4 border-green-500 mx-auto"
                      />
                      <div className="absolute -bottom-2 -right-2 bg-green-500 text-white rounded-full p-2">
                        <Check className="w-5 h-5" />
                      </div>
                      <p className="text-center text-sm text-green-600 mt-3 font-medium">
                        Photo uploaded - Tap to change
                      </p>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors bg-white">
                      {Capacitor.isNativePlatform() ? (
                        <Camera className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                      ) : (
                        <Upload className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                      )}
                      <p className="text-gray-700 font-medium">
                        {Capacitor.isNativePlatform() ? 'Tap to take photo' : 'Click to upload photo'}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Use a clear, well-lit photo showing your face
                      </p>
                    </div>
                  )}
                </label>
              </div>

              {/* Show full form for vehicle drivers */}
              {driverType === 'vehicle' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vehicleMake" className="text-gray-700">Vehicle Make *</Label>
                      <Input
                        id="vehicleMake"
                        type="text"
                        placeholder="e.g., Toyota"
                        value={formData.vehicleMake}
                        onChange={(e) => setFormData({...formData, vehicleMake: e.target.value})}
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vehicleModel" className="text-gray-700">Vehicle Model *</Label>
                      <Input
                        id="vehicleModel"
                        type="text"
                        placeholder="e.g., Corolla"
                        value={formData.vehicleModel}
                        onChange={(e) => setFormData({...formData, vehicleModel: e.target.value})}
                        className="mt-2"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vehiclePlate" className="text-gray-700">License Plate *</Label>
                      <Input
                        id="vehiclePlate"
                        type="text"
                        placeholder="e.g., ABC 1234"
                        value={formData.vehiclePlate}
                        onChange={(e) => setFormData({...formData, vehiclePlate: e.target.value})}
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vehicleColor" className="text-gray-700">Color *</Label>
                      <Input
                        id="vehicleColor"
                        type="text"
                        placeholder="e.g., White"
                        value={formData.vehicleColor}
                        onChange={(e) => setFormData({...formData, vehicleColor: e.target.value})}
                        className="mt-2"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="vehicleYear" className="text-gray-700">Year of Make *</Label>
                    <Input
                      id="vehicleYear"
                      type="text"
                      placeholder="e.g., 2020"
                      value={formData.vehicleYear}
                      onChange={(e) => setFormData({...formData, vehicleYear: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                      className="mt-2"
                      required
                    />
                  </div>

                  {/* Vehicle Category Selection */}
                  <div>
                    <Label className="text-gray-700 mb-3 block">Vehicle Category *</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, vehicleCategory: 'economy'})}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          formData.vehicleCategory === 'economy'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${
                            formData.vehicleCategory === 'economy' ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            Economy
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Standard pricing</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, vehicleCategory: 'premium'})}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          formData.vehicleCategory === 'premium'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${
                            formData.vehicleCategory === 'premium' ? 'text-blue-600' : 'text-gray-600'
                          }`}>
                            Premium
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Higher earnings</p>
                        </div>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Premium vehicles offer higher earnings but must meet quality standards
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Label className="text-gray-700 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Vehicle Images *
                    </Label>
                    <p className="text-sm text-gray-500">
                      {Capacitor.isNativePlatform() 
                        ? 'Tap to take photo or choose from gallery' 
                        : 'Click to upload photos'}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {['front', 'side', 'back'].map((position) => (
                        <div key={position} className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-500 transition-colors">
                          {!Capacitor.isNativePlatform() && (
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(position, e)}
                              className="hidden"
                              id={`image-${position}`}
                            />
                          )}
                          <label 
                            htmlFor={Capacitor.isNativePlatform() ? undefined : `image-${position}`} 
                            className="cursor-pointer block"
                            onClick={Capacitor.isNativePlatform() ? () => handleImageUpload(position, null) : undefined}
                          >
                            {vehicleImages[position] ? (
                              <div className="relative">
                                <img 
                                  src={vehicleImages[position]} 
                                  alt={`Vehicle ${position}`} 
                                  className="w-full h-32 object-cover rounded" 
                                />
                                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                {Capacitor.isNativePlatform() ? (
                                  <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                ) : (
                                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                )}
                                <p className="text-sm text-gray-600 capitalize">{position} View</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {Capacitor.isNativePlatform() ? 'Tap to capture' : 'Click to upload'}
                                </p>
                              </div>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Simplified form for bike drivers */}
              {driverType === 'bike' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Bike Registration:</strong> You only need to provide your bike registration/plate number. 
                      Vehicle photos, year of make, and driver's license are not required for bike drivers.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vehiclePlate" className="text-gray-700">Bike Registration/Plate Number *</Label>
                      <Input
                        id="vehiclePlate"
                        type="text"
                        placeholder="e.g., ABC 1234"
                        value={formData.vehiclePlate}
                        onChange={(e) => setFormData({...formData, vehiclePlate: e.target.value})}
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vehicleModel" className="text-gray-700">Bike Model (Optional)</Label>
                      <Input
                        id="vehicleModel"
                        type="text"
                        placeholder="e.g., Honda CBR"
                        value={formData.vehicleModel}
                        onChange={(e) => setFormData({...formData, vehicleModel: e.target.value})}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="vehicleColor" className="text-gray-700">Color (Optional)</Label>
                    <Input
                      id="vehicleColor"
                      type="text"
                      placeholder="e.g., Red"
                      value={formData.vehicleColor}
                      onChange={(e) => setFormData({...formData, vehicleColor: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                </>
              )}

              <Button 
                onClick={handleCompleteRegistration}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                disabled={loading}
              >
                {loading ? 'Registering...' : 'Complete Registration'}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default EnhancedDriverAuth;
